"use client";
import React, { useEffect, useState } from "react";
import Link from "next/link";
import { motion } from "framer-motion";

const NotFound = () => {
  const [glitchText, setGlitchText] = useState("404");
  const [dimensions, setDimensions] = useState({ width: 1920, height: 1080 });

  useEffect(() => {
    // Set initial dimensions on client side
    setDimensions({
      width: window.innerWidth,
      height: window.innerHeight,
    });

    const glitchChars = "!<>-_\\/[]{}—=+*^?#________404";
    const interval = setInterval(() => {
      setGlitchText(
        Math.random() > 0.8
          ? glitchChars[Math.floor(Math.random() * glitchChars.length)].repeat(
              3
            )
          : "404"
      );
    }, 150);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative flex h-screen w-full flex-col items-center justify-center overflow-hidden bg-[#0E1016]">
      {/* Animated background grid */}

      {/* Floating particles */}
      {[...Array(20)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute h-1 w-1 rounded-full bg-[#e4ded7]"
          initial={{
            x: Math.random() * dimensions.width,
            y: Math.random() * dimensions.height,
            opacity: Math.random() * 0.5,
          }}
          animate={{
            y: [null, Math.random() * dimensions.height],
            opacity: [null, 0],
          }}
          transition={{
            duration: Math.random() * 10 + 10,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      ))}

      {/* Main content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 flex flex-col items-center gap-8 px-4"
      >
        {/* Glitch 404 */}
        <div className="relative">
          <motion.h1
            className="text-[80px] font-bold text-[#e4ded7] sm:text-[100px] md:text-[120px] lg:text-[140px]"
            animate={{
              textShadow: [
                "0 0 20px rgba(228, 222, 215, 0.5)",
                "0 0 40px rgba(228, 222, 215, 0.8)",
                "0 0 20px rgba(228, 222, 215, 0.5)",
              ],
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            {glitchText}
          </motion.h1>

          {/* Glitch effect layers */}
          <motion.div
            className="absolute inset-0 text-[80px] font-bold text-red-500 opacity-70 sm:text-[100px] md:text-[120px] lg:text-[140px]"
            animate={{
              x: [0, -5, 5, 0],
              opacity: [0, 0.7, 0],
            }}
            transition={{
              duration: 0.3,
              repeat: Infinity,
              repeatDelay: Math.random() * 3,
            }}
          >
            404
          </motion.div>
          <motion.div
            className="absolute inset-0 text-[80px] font-bold text-cyan-500 opacity-70 sm:text-[100px] md:text-[120px] lg:text-[140px]"
            animate={{
              x: [0, 5, -5, 0],
              opacity: [0, 0.7, 0],
            }}
            transition={{
              duration: 0.3,
              repeat: Infinity,
              repeatDelay: Math.random() * 3,
            }}
          >
            404
          </motion.div>
        </div>

        {/* Error messages */}
        <div className="flex flex-col items-center gap-4 text-center">
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-2xl font-semibold text-[#e4ded7] sm:text-3xl md:text-4xl"
          >
            Page introuvable
          </motion.h2>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="max-w-md text-lg text-[#bdbdbd] sm:text-xl"
          >
            Cette page semble avoir disparu dans le vide numérique...
          </motion.p>

          {/* Code snippet easter egg */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="mt-4 rounded-lg border border-[#bdbdbd]/20 bg-black/30 p-4 font-mono text-sm text-[#bdbdbd] backdrop-blur-sm"
          >
            <code className="block">
              <span className="text-purple-400">const</span>{" "}
              <span className="text-blue-400">page</span> ={" "}
              <span className="text-orange-400">null</span>;
              <br />
              <span className="text-red-400">
                // Error: Cannot read property of undefined
              </span>
            </code>
          </motion.div>
        </div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="mt-8 flex flex-col gap-4 sm:flex-row"
        >
          <Link href="/">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="group relative overflow-hidden rounded-lg border border-[#e4ded7] bg-[#e4ded7] px-8 py-4 font-semibold text-[#0E1016] transition-all duration-300"
            >
              <span className="relative z-10">Retour à l'accueil</span>
              <motion.div
                className="absolute inset-0 bg-white"
                initial={{ x: "-100%" }}
                whileHover={{ x: 0 }}
                transition={{ duration: 0.3 }}
              />
            </motion.button>
          </Link>

          <Link href="/#contact">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="rounded-lg border border-[#e4ded7]/50 bg-transparent px-8 py-4 font-semibold text-[#e4ded7] transition-all duration-300 hover:border-[#e4ded7] hover:bg-[#e4ded7]/10"
            >
              Me contacter
            </motion.button>
          </Link>
        </motion.div>

        {/* Fun hint */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.5, 0] }}
          transition={{ delay: 2, duration: 3, repeat: Infinity }}
          className="mt-8 text-sm text-[#bdbdbd]/50"
        >
          Astuce : Vérifie l'URL ou utilise la navigation
        </motion.p>
      </motion.div>

      {/* Scanline effect */}
      <motion.div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-[#e4ded7]/5 to-transparent"
        animate={{ y: ["-100%", "100%"] }}
        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
      />
    </div>
  );
};

export default NotFound;
